import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { styled } from '@mui/material/styles'
import theme from './theme'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import Checkbox from '@mui/material/Checkbox'
import { useTranslation } from 'react-i18next'
import { useWindowDimensions } from '../../services/hooks/useWindowDimensions'
import { t } from 'i18next'

function isEmpty(obj) {
  return Object.keys(obj).length === 0
}

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function stableSort(array, pinnedQueue, comparator) {
  let arrayOne
  let arrayTwo
  if (isEmpty(pinnedQueue)) {
    arrayOne = []
    arrayTwo = [...array]
  } else {
    arrayOne = array.filter(item => item.queueId === pinnedQueue.id)
    arrayTwo = array.filter(item => item.queueId !== pinnedQueue.id)
  }

  const stabilizedOne = arrayOne.map((el, index) => [el, index])
  stabilizedOne.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })

  const stabilizedTwo = arrayTwo.map((el, index) => [el, index])
  stabilizedTwo.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedOne.map((el) => el[0]).concat(stabilizedTwo.map((el) => el[0]))
}

const headCells = [
  {
    id: 'queue',
    numeric: false,
    disablePadding: false,
    label: t('Queue Name'),
  },
  {
    id: 'job',
    numeric: false,
    disablePadding: false,
    label: t('Document Name'),
  },
  // {
  //   id: 'pages',
  //   numeric: true,
  //   disablePadding: false,
  //   label: t('Pages'),
  // },
  {
    id: 'size',
    numeric: true,
    disablePadding: false,
    label: t('File Size'),
  },
  {
    id: 'submitted',
    numeric: true,
    disablePadding: false,
    label: t('Submitted'),
  },
];

const CustomTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontSize: 14,
    // fontWeight: 'bold',
    fontWeight: 'normal',
    fontFamily: 'Tahoma'
  },
  [`&.${tableCellClasses.body}`]: {
    
  },
}))

const CustomTableRow = styled(TableRow)(() => ({
  // '&:nth-of-type(odd)': {
  //   backgroundColor: theme.palette.action.hover,
  // },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}))

function CustomTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow >
        <CustomTableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{
              'aria-label': 'select all',
            }}
          />
        </CustomTableCell>
        {headCells.map((headCell) => (
          <CustomTableCell
            key={headCell.id}
            align={'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </CustomTableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function PrintJobs(props) {
  const { optimizedJobs, univPrintJobs, handleSelectAllClick, handleSelectClick, selected, pinnedQueue } = props
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('queue')
  const { t } = useTranslation()
  const { width } = useWindowDimensions()

  const useStyles = makeStyles({

    card: {
        display: 'flex',
        flexDirection: 'column',
        width: '100vw',
        height: '72vh',
        position: 'absolute',
        top: width <= 800 ? '26.3%' : '23.8%',
        left: '0.0%',
        zIndex: '5'
    }
  })
  const classes = useStyles()

  const rows = optimizedJobs.length > 0 ?
    optimizedJobs.map((job) => {
      return {
        id: job.id,
        queue: job.queue.name,
        job: job.name,
        pages: job.pageCount,
        size: job.fileSize,
        submitted: job.dateCreated,
        queueId: job.queue.id
      }
    }) : []

  if (univPrintJobs.length > 0) {
    const universalPrintJobs = univPrintJobs.map(item => {
      return {
        id: item.id,
        documentId: item.documentId,
        job: item.fileName,
        size: item.size,
        submitted: item.uploadedAt,
        queue: t('Universal Print'),
        isUnivPrintJob: true
      }
    })
    rows.push(...universalPrintJobs)
  }
  // console.log('PrintJobs.jobs:', jobs)
  // console.log('PrintJobs.optimizedJobs:', optimizedJobs)
  // console.log('PrintJobs.queues:', queues)
  // console.log('PrintJobs.pinnedQueue:', pinnedQueue)

  useEffect(() => {
    if (isEmpty(pinnedQueue)) {
      handleRequestSort('queue')
    } else {
      handleRequestSort(orderBy)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedQueue])

  function getTimePosted(date) {
    const now = new Date()
    const input = new Date(date)

    const inYear = input.getFullYear()
    const inMonth = input.getMonth()
    const inDay = input.getDate()

    const diff = now - input
  
    const seconds = Math.floor(diff / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (days > 0) {
      const output = [(inMonth + 1) > 9 ? (inMonth + 1) : '0' + (inMonth + 1), inDay > 9 ? inDay : '0' + inDay, inYear].join('-') + ' ' +
        input.toLocaleTimeString('en-US', {hour12: false, hour: '2-digit', minute: '2-digit'})
      return output
    } else if (hours > 0) {
      const output = hours.toString() + ' h'
      + (Math.floor((minutes/60 - hours) * 60) === 0 ? '' : ', ' + Math.floor((minutes/60 - hours) * 60) + t(' min ago'))
      return output
    } else if (minutes > 0) {
      const output = minutes + t(' min ago')
      return output
    } else {
      return t('Just now')
    }
  }

  function getFileSize(bytes) {
    if (bytes < 1024) {
      return bytes + ' Bytes'
    } else if (bytes < 1024 * 1024) {
      return Math.round((bytes / 1024) * 100) / 100 + ' Kb'
    } else if (bytes < 1024 * 1024 * 1024) {
      return Math.round((bytes / (1024 * 1024)) * 100) / 100 + ' Mb'
    } else if (bytes < 1024 * 1024 * 1024 * 1024) {
      return Math.round((bytes / (1024 * 1024 * 1024)) * 100) / 100 + ' Gb'
    } else {
      return Math.round((bytes / (1024 * 1024 * 1024 * 1024)) * 100) / 100 + ' Tb'
    }
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const isSelected = (id) => selected[id] !== undefined;

  const visibleRows = useMemo(
    () => stableSort(rows, pinnedQueue, getComparator(order, orderBy)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order, orderBy, rows.length],
  );

  return (
    <div className={`${classes.card}`}>
      <TableContainer>
        <Table
          stickyHeader
          size='small'
        >
          <CustomTableHead
            numSelected={Object.keys(selected).length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
          />
          <TableBody>
            {visibleRows.map((row, index) => {
              const isItemSelected = isSelected(row.id);
              const labelId = `enhanced-table-checkbox-${index}`;

              return (
                <CustomTableRow
                  hover
                  onClick={(event) => handleSelectClick(event, row)}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  key={row.id}
                  selected={isItemSelected}
                  sx={{ 
                    cursor: 'pointer', 
                    backgroundColor: !isEmpty(pinnedQueue) && row.queueId === pinnedQueue.id ? 
                      'lightgray' : 
                      row?.isUnivPrintJob ? 
                        'rgba(127, 101, 183, 0.1)': 
                        'inherit',
                  }}
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      checked={isItemSelected}
                      inputProps={{
                        'aria-labelledby': labelId,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    component="th"
                    id={labelId}
                    scope="row"
                    padding="none"
                  >
                    {row.queue}
                  </TableCell>
                  <TableCell align="left">{row.job}</TableCell>
                  {/* <TableCell align="left">{row.pageCount ? row.pageCount : '—'}</TableCell> */}
                  <TableCell align="left">{row?.isUnivPrintJob && row.size === 0 ? t('Pending') : getFileSize(row.size)}</TableCell>
                  <TableCell align="left">{row?.isUnivPrintJob && row.submitted === '0001-01-01T00:00:00Z' ? t('Pending') : getTimePosted(row.submitted)}</TableCell>
                </CustomTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}